import { ApiError, type ContactPayload, type ContactSuccess, type SiteContent } from './types';

/**
 * Empty base URL means same-origin `/api`, which is how both compose files are
 * wired (nginx proxies, or Vite proxies in development). Set VITE_API_BASE_URL
 * only when the API lives on a different host.
 */
const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

function url(path: string): string {
  return `${baseUrl}/api${path}`;
}

async function parseError(response: Response): Promise<ApiError> {
  let message = `Request failed (${response.status}).`;
  let fields: Record<string, string> = {};
  let explained = false;

  try {
    const body = (await response.json()) as { error?: string; fields?: Record<string, string> };
    if (body.error) {
      message = body.error;
      // Only our own API produces this shape, and it is already localised.
      explained = true;
    }
    if (body.fields) fields = body.fields;
  } catch {
    // Not JSON: a proxy error page or an empty body. `message` stays the bare
    // status line and `explained` stays false so no caller shows it to a person.
  }

  return new ApiError(response.status, message, fields, explained);
}

export async function fetchContent(signal?: AbortSignal): Promise<SiteContent> {
  const response = await fetch(url('/content'), {
    headers: { accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) throw await parseError(response);
  return (await response.json()) as SiteContent;
}

export async function submitContact(payload: ContactPayload): Promise<ContactSuccess> {
  const response = await fetch(url('/contact'), {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw await parseError(response);
  return (await response.json()) as ContactSuccess;
}
